export interface WorkflowReportPresentation {
  key: string;
  parentTable: 'merchant' | 'submission';
  sidebarLabel: string;
  contextLabel: string;
  /**
   * Submission submenus render the Submissions list grid by default (their
   * rows are submissions). 'report' renders the plain report grid, for a
   * submenu whose rows are not submissions (Send Failed: one row per failed
   * funder email, which may outlive its submission).
   */
  grid?: 'submission_list' | 'report';
}

/**
 * Curated placement and presentation for workflow reports.
 *
 * The database report owns the query and access policy. This list only places
 * registered reports beneath their related core route and gives each report
 * page a fixed context badge.
 */
export const WORKFLOW_REPORTS: WorkflowReportPresentation[] = [
  {
    key: 'unsubmitted_merchants',
    parentTable: 'merchant',
    sidebarLabel: 'Unsubmitted',
    contextLabel: 'Unsubmitted',
  },
  {
    key: 'merchant_missing_documents',
    parentTable: 'merchant',
    sidebarLabel: 'Missing Documents',
    contextLabel: 'Missing Documents',
  },
  {
    key: 'unresponsive_submissions',
    parentTable: 'submission',
    sidebarLabel: 'Unresponsive',
    contextLabel: 'No Response',
  },
  {
    key: 'awaiting_documents',
    parentTable: 'submission',
    sidebarLabel: 'Awaiting Documents',
    contextLabel: 'Awaiting Documents',
  },
  // REG-954: funder emails that failed and are not resolved yet.
  {
    key: 'send_failed_submissions',
    parentTable: 'submission',
    sidebarLabel: 'Send Failed',
    contextLabel: 'Send Failed',
    grid: 'report',
  },
];

export const WORKFLOW_REPORT_BY_KEY = Object.fromEntries(
  WORKFLOW_REPORTS.map((report) => [report.key, report]),
) as Record<string, WorkflowReportPresentation>;

/** True when the report is a Submissions submenu shown in the Submissions list grid. */
export function rendersSubmissionList(reportKey: string | null | undefined): boolean {
  const report = reportKey ? WORKFLOW_REPORT_BY_KEY[reportKey] : undefined;
  return report?.parentTable === 'submission' && (report.grid ?? 'submission_list') === 'submission_list';
}
